/**
 * contractCompatibility — pure resolver utilities for the ContractAssignModal.
 * Extracted from `ContractAssignModal.jsx` (04/06/2026 refactor) so the
 * component file can focus on presentation. ZERO behavior change.
 *
 * Three families of checks :
 *  - vehicleSatisfiesContract(vehicle, contract)   → { ok, reason?, value? }
 *  - driverSatisfiesContract(driver,  contract)    → { ok, reason?, value? }
 *  - driverCanDriveVehicle (driver,  vehicle)      → { ok, reason?, required?, owned? }
 *  - hasSpecialtyMatch     (driver,  contract)     → boolean (bonus eligibility)
 *
 * The exported `CATEGORY_VEHICLE_REQUIREMENTS` dictionary is also reused by
 * the modal to display the "Équipement requis" badge.
 *
 * The dictionary keys ('BTP / Matériaux', 'Spécialisé', etc.) and the values
 * inside the arrays are *data lookup keys* — they MUST match the FR labels
 * stored on `vehicle.specialties`, `contract.category` and the driver
 * specialty data. They are NOT user-facing strings. Marked i18n-keep.
 */
import { getDriverSpecialties } from './driverSpecialties';
import { getDepotEffectiveInvestments } from '../data/depotPhases';

// Aliases for driver specialty bonus (+15%). These are NOT hard gates; they
// simply trigger the revenue bonus when the driver's specialty matches the
// contract's category. The hard vehicle gate is below.
export const SPECIALTY_ALIASES = {
  // Long category labels (used by `contract.category`)
  'Messagerie / Colis': ['Messagerie', 'Messagerie / Colis', 'Messagerie/Colis', 'Messagerie express', 'Colis'], // i18n-keep
  'BTP / Matériaux': ['BTP', 'BTP / Matériaux', 'BTP/Matériaux/Engins TP', 'BTP lourd', 'Chantier', 'Engins de chantier'], // i18n-keep
  'Spécialisé': ['ADR', 'ADR/Produits sensibles', 'Spécialisé', 'Matières dangereuses', 'Citerne'], // i18n-keep
  'Export EU': ['Export EU', 'Export UE', 'Longue distance', 'EU', 'International'],
  // 06/02/2026 — P0 prod bug fix : contract.category = 'Frigo/Pharma' (cf.
  // data/contracts.js) ne matchait pas la clé 'Frigorifique' → le gate
  // équipement frigo était silencieusement skippé, le joueur pouvait
  // assigner n'importe quel camion à une livraison GMS frais. Alignement
  // sur la valeur canonique exposée par les contrats et les véhicules.
  'Frigo/Pharma': ['Frigo/Pharma', 'Frigorifique', 'GMS', 'Pharma'],
  'Frigorifique': ['Frigorifique', 'Frigo/Pharma', 'GMS', 'Pharma'],
  'Vrac / Palettes': ['Vrac', 'Vrac / Palettes', 'Vrac/Palettes', 'Vrac lourd', 'Palettes', 'Distribution', 'National'],
  'Vrac/Palettes': ['Vrac', 'Vrac / Palettes', 'Vrac/Palettes', 'Vrac lourd', 'Palettes', 'Distribution', 'National'],
  'Export UE': ['Export UE', 'Export EU', 'Longue distance', 'EU', 'International'],
  'Export Extra-UE': ['Export UE', 'Export EU', 'Longue distance', 'International'],
  'RSE / Marchés publics': ['Export UE', 'Vrac/Palettes', 'Messagerie/Colis', 'Frigo/Pharma'],
  // Short keys (used by `contract.requiresSpecialty` on SPECIAL_CONTRACTS)
  'BTP': ['BTP', 'BTP / Matériaux', 'BTP/Matériaux/Engins TP', 'BTP lourd', 'Chantier', 'Engins de chantier'], // i18n-keep
  'BTP/Matériaux/Engins TP': ['BTP', 'BTP / Matériaux', 'BTP/Matériaux/Engins TP', 'BTP lourd', 'Chantier', 'Engins de chantier'], // i18n-keep
  'Messagerie': ['Messagerie', 'Messagerie / Colis', 'Messagerie/Colis', 'Messagerie express', 'Colis'], // i18n-keep
  'Messagerie/Colis': ['Messagerie', 'Messagerie / Colis', 'Messagerie/Colis', 'Messagerie express', 'Colis'], // i18n-keep
  'Frigo': ['Frigorifique', 'Frigo/Pharma', 'GMS', 'Pharma'],
  'ADR': ['ADR', 'ADR/Produits sensibles', 'Spécialisé', 'Matières dangereuses', 'Citerne'], // i18n-keep
};

// Hard vehicle equipment requirements per contract category. If a list is
// defined, the vehicle must carry at least one of these specialties (e.g. a
// frigo contract requires a refrigerated truck — non-frigo trucks cannot
// transport perishable goods regardless of tier).
// Empty list / missing key = general cargo, no equipment constraint beyond tier.
// 06/02/2026 — P0 fix : keys MUST match the actual values stored in
// `contract.category` (cf. data/contracts.js). The legacy 'Frigorifique'
// and 'Export EU' keys are kept for backward compatibility with existing
// tests (canExecuteNow.test.js feeds them as inputs).
export const CATEGORY_VEHICLE_REQUIREMENTS = {
  // Canonical contract.category values (this is what the runtime checks).
  'Frigo/Pharma': ['Frigo/Pharma', 'Frigorifique', 'GMS', 'Pharma'],
  'Export UE': ['Export UE', 'Export EU', 'Longue distance', 'International'],
  'Export Extra-UE': ['Export UE', 'Export EU', 'Longue distance', 'International'],
  'BTP / Matériaux': ['BTP', 'BTP/Matériaux/Engins TP', 'BTP lourd', 'Benne', 'Chantier', 'Engins de chantier', 'Vrac lourd'], // i18n-keep
  'Spécialisé': ['ADR', 'ADR/Produits sensibles', 'Spécialisé', 'Matières dangereuses', 'Citerne'], // i18n-keep
  // Legacy aliases — kept so canExecuteNow.test.js (which feeds 'Frigorifique'
  // explicitly) still passes. Real contracts in data/contracts.js never use
  // these keys, but third-party data could.
  'Frigorifique': ['Frigo/Pharma', 'Frigorifique', 'GMS', 'Pharma'],
  'Export EU': ['Export UE', 'Export EU', 'Longue distance', 'International'],
};

// Hard driver-specialty gate: some contract categories require the driver to
// hold the matching qualification (frigo for refrigerated, ADR for hazardous,
// etc.). Conducting a refrigerated load without HACCP training is not legal —
// so we treat these specialties as a strict requirement, not a bonus.
export const CATEGORY_DRIVER_REQUIREMENTS = {
  // Canonical contract.category values.
  'Frigo/Pharma': ['Frigo/Pharma', 'Frigorifique', 'GMS', 'Pharma'],
  'Spécialisé': ['ADR', 'ADR/Produits sensibles', 'Spécialisé', 'Matières dangereuses'], // i18n-keep
  'BTP / Matériaux': ['BTP', 'BTP/Matériaux/Engins TP', 'BTP lourd', 'Chantier'], // i18n-keep
  'Export UE': ['Export UE', 'Export EU', 'Longue distance', 'EU'],
  'Export Extra-UE': ['Export UE', 'Export EU', 'Longue distance', 'EU'],
  // Legacy aliases for tests / external data.
  'Frigorifique': ['Frigo/Pharma', 'Frigorifique', 'GMS', 'Pharma'],
  'Export EU': ['Export UE', 'Export EU', 'Longue distance', 'EU'],
};

// 13/06/2026 — Human-friendly specialty labels for the blocked-driver
// message. Without this, the modal printed « Spécialité Spécialisé requise »
// (the raw category name). We now surface the actual qualification needed.
export const CATEGORY_SPECIALTY_LABELS = {
  'Spécialisé': 'ADR / Matières dangereuses', // i18n-keep
  'Frigo/Pharma': 'Frigo / Pharma',
  'Frigorifique': 'Frigorifique',
  'BTP / Matériaux': 'BTP', // i18n-keep
  'Export UE': 'Longue distance / Export UE',
  'Export EU': 'Longue distance / Export UE',
  'Export Extra-UE': 'Longue distance / Export Extra-UE',
};

// Permit ladder — a driver holding a higher class can drive vehicles that
// only require a lower class. Real-world mapping kept simple:
//   B  → light commercial only (PTAC ≤ 3.5 t)
//   C1 → 3.5–7.5 t (we model this as a strict subset of C)
//   C  → > 7.5 t solo (T2 in HST)
//   CE → > 7.5 t + remorque (T3+ in HST)
export const PERMIT_RANK = { B: 0, C1: 1, C: 2, CE: 3 };

/**
 * 13/06/2026 — Obligation d'investir dans la zone frigorifique du dépôt à
 * partir du tiers 3 (retour utilisateur). Les contrats frigo T3+ exigent
 * l'investissement `inv-depot-05` (Zone frigorifique au dépôt) au QG, OU
 * une « Zone frigo locale » (évolution `frigoLocal`) sur n'importe quel
 * dépôt du réseau (Chantier Dépôts).
 */
export function frigoZoneBlocked(contract, activeInvestments, ownedDepots = []) {
  if (!contract) return false;
  if (contract.category !== 'Frigorifique' || (contract.tierMin || 1) < 3) return false;
  if ((activeInvestments || {})['inv-depot-05']) return false;
  // 13/07/2026 — Progression unifiée : zone frigo d'un dépôt secondaire =
  // inv-depot-05 acheté pour ce dépôt (legacy `frigoLocal` mappé).
  if ((ownedDepots || []).some(d => getDepotEffectiveInvestments(d)['inv-depot-05'])) return false;
  return true;
}

export function hasSpecialtyMatch(driver, contract) {
  if (!driver || !contract) return false;
  const aliases = SPECIALTY_ALIASES[contract.category] || [contract.category];
  const driverSpecs = getDriverSpecialties(driver); // includes extraSpecialties
  return driverSpecs.some(s => aliases.includes(s));
}

export function driverSatisfiesContract(driver, contract) {
  // 24/07/2026 — contrats locaux export : seul un chauffeur LOCAL du pays
  // du contrat peut être affecté (recrutement local RH).
  if (contract.requiresLocalCountry && driver.localCountry !== contract.requiresLocalCountry) {
    return { ok: false, reason: 'local_driver', value: contract.requiresLocalCountry };
  }
  // Hard license requirement (special contracts). 20/07/2026 — hiérarchique :
  // un permis supérieur couvre l'exigence (CE satisfait C), comme dans la
  // vraie vie et comme driverCanDriveVehicle.
  if (contract.requiresLicense) {
    const licenses = driver.licenses || ['B'];
    const requiredRank = PERMIT_RANK[contract.requiresLicense] ?? 0;
    const bestOwnedRank = licenses.reduce((acc, lic) => Math.max(acc, PERMIT_RANK[lic] ?? 0), 0);
    if (bestOwnedRank < requiredRank) {
      return { ok: false, reason: 'license', value: contract.requiresLicense };
    }
  }
  // Hard specialty requirement on certain categories (frigo, ADR, BTP, EU).
  // BUG fix 22/05/2026: use getDriverSpecialties so extraSpecialties (slot 3+
  // unlocked through paid training) count for qualification gates. Previously
  // a driver with 3+ specialties was treated as if they only owned the legacy
  // slot-1/slot-2, which made the 3rd/4th training feel useless.
  const requiredSpec = CATEGORY_DRIVER_REQUIREMENTS[contract.category];
  if (requiredSpec) {
    const specs = getDriverSpecialties(driver);
    const ok = specs.some(s => requiredSpec.includes(s));
    if (!ok) {
      return {
        ok: false,
        reason: 'driver_specialty',
        value: CATEGORY_SPECIALTY_LABELS[contract.category] || contract.category,
      };
    }
  }
  // 20/07/2026 — user prod : `requiresSpecialty` est une exigence
  // d'ÉQUIPEMENT VÉHICULE (cf. vehicleSatisfiesContract), plus un gate
  // chauffeur. L'ancien double-gate rendait sp-01 (« Urgence médecine
  // nucléaire ») IMPOSSIBLE : le chauffeur devait cumuler ADR (catégorie
  // Spécialisé) ET Frigo/Pharma. La certification chauffeur reste exigée
  // via CATEGORY_DRIVER_REQUIREMENTS (ADR pour Spécialisé, frigo pour
  // Frigo/Pharma, etc.).
  return { ok: true };
}

/**
 * Hard cross-check: a driver may only operate a vehicle whose required
 * permit they hold (or any higher class). Surface a clear "permis manquant"
 * message in the modal so the user understands why the green button stays
 * disabled.
 */
export function driverCanDriveVehicle(driver, vehicle) {
  if (!driver || !vehicle) return { ok: true };
  const required = vehicle.license || 'B';
  const owned = driver.licenses || ['B'];
  // Direct match — fast path covers e.g. B for T1, C for T2.
  if (owned.includes(required)) return { ok: true };
  // Hierarchical match — owning a higher class permit implies lower ones.
  const requiredRank = PERMIT_RANK[required] ?? 0;
  const bestOwnedRank = owned.reduce((acc, lic) => Math.max(acc, PERMIT_RANK[lic] ?? 0), 0);
  if (bestOwnedRank >= requiredRank) return { ok: true };
  return { ok: false, reason: 'license_missing', required, owned };
}

export function vehicleSatisfiesContract(vehicle, contract) {
  // Tier minimum (always enforced — and now strictly tier ≥ tierMin so higher
  // tiers naturally fulfil lower-tier contracts).
  if ((vehicle.tier || 0) < (contract.tierMin || 1)) {
    return { ok: false, reason: 'tier', value: contract.tierMin };
  }

  // Hard equipment requirement. 20/07/2026 — user prod : quand le contrat
  // porte un `requiresSpecialty` explicite (contrats spéciaux sp-XX), il
  // REMPLACE le gate d'équipement de la catégorie au lieu de s'y CUMULER.
  // Avant, sp-01 (« Urgence médecine nucléaire », catégorie Spécialisé +
  // requiresSpecialty Frigo/Pharma) exigeait un camion ADR **ET** frigo —
  // combinaison qui n'existe pas au catalogue → contrat impossible à
  // accepter. Désormais : camion FRIGO exigé (l'ADR est porté par le
  // chauffeur via CATEGORY_DRIVER_REQUIREMENTS).
  if (contract.requiresSpecialty) {
    const aliases = SPECIALTY_ALIASES[contract.requiresSpecialty] || [contract.requiresSpecialty];
    const vSpecs = vehicle.specialties || [];
    const ok = vSpecs.some(s => aliases.includes(s));
    if (!ok) {
      return { ok: false, reason: 'specialty', value: contract.requiresSpecialty };
    }
    return { ok: true };
  }

  // Category equipment requirement (e.g. frigo for refrigerated cargo).
  const required = CATEGORY_VEHICLE_REQUIREMENTS[contract.category];
  if (required && required.length > 0) {
    const vSpecs = vehicle.specialties || [];
    const hasRequiredEquipment = vSpecs.some(s => required.includes(s));
    if (!hasRequiredEquipment) {
      return { ok: false, reason: 'equipment', value: contract.category };
    }
  }

  // contract.vehicleRequired (legacy "preferred model") is now a SOFT hint
  // only — not enforced. Higher tiers + any compatible equipment are allowed.

  return { ok: true };
}
